import { useState, useEffect } from 'react';
import { Info, Zap, History, Save, Image as ImageIcon, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/button';
import { InlineText, InlineImage, InlineArray, SectionCard as InlineSectionCard } from '../../components/cms/InlineEditors';
import RichTextEditor from '../../components/cms/RichTextEditor';
import { VersionHistoryViewer } from '../../components/cms/VersionHistoryViewer';
import versionHistoryService from '../../services/versionHistoryService';
import { authService } from '../../services/authService';
import adminApi from '../../services/adminApi';
import cmsApiService from '../../services/cmsApiService';

interface FeatureItem {
  [key: string]: unknown;
  id?: number;
  name: string;
  subname: string;
  iconUrl: string;
  _isNew?: boolean;      // Track if this is a newly added feature
  _isEdited?: boolean;   // Track if this existing feature was edited
}

interface PageContentSection {
  id?: number;
  pageName: string;
  sectionName: string;
  attributes: Record<string, unknown>;
}

interface AboutSectionResponse {
  id: number;
  sectionName: string;
  title: string;
  subTitle: string;
  description: string;
  bgImageUrl: string;
  buttonName: string;
  videoUrl: string;
  videoTitle: string;
  ctaSubTitle: string;
  ctaTitle: string;
  ctaButtonName: string;
  ctaBgImageUrl: string;
  images: Array<{
    id?: number;
    name: string;
    subName: string;
    imageUrl: string;
  }>;
}

export default function NewAboutPageManager() {
  const [historyDialog, setHistoryDialog] = useState<{ type: string; id: number } | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [aboutSectionId, setAboutSectionId] = useState<number | null>(null);

  // Section 1: About Main Content
  const [aboutMain, setAboutMain] = useState<PageContentSection>({
    pageName: 'about',
    sectionName: 'ABOUT',
    attributes: {
      subtitle: 'About Us',
      title: 'Grand Pittu Story',
      description: 'Since 1995, we have been serving authentic...',
      mainImageUrl: '',
      buttonText: 'View Menu',
      features: [] as FeatureItem[],
    },
  });

  // Section 2: Video Player
  const [videoSection, setVideoSection] = useState<PageContentSection>({
    pageName: 'about',
    sectionName: 'ABOUT',
    attributes: {
      youtubeUrl: '',
      title: 'Our Restaurant Tour',
    },
  });

  // Section 4: CTA Section
  const [ctaSection, setCtaSection] = useState<PageContentSection>({
    pageName: 'about',
    sectionName: 'ABOUT',
    attributes: {
      subtitle: 'Reserve Now',
      title: 'Book Your Table Today',
      buttonText: 'Make Reservation',
      backgroundImageUrl: '',
    },
  });

  useEffect(() => {
    loadAboutSection();
  }, []);

  const loadAboutSection = async () => {
    setIsLoading(true);
    
    try {
      const response = await cmsApiService.getAboutSectionByName('ABOUT');
      
      if (!response) {
        toast('No about section found. Using default values.', { icon: 'i' });
        setIsLoading(false);
        return;
      }

      const data: AboutSectionResponse = response;
      
      if (!data) {
        setIsLoading(false);
        return;
      }

      if (data.id) {
        setAboutSectionId(data.id);
      }

      // Map backend images to frontend features (no _isNew or _isEdited flags for loaded items)
      const mappedFeatures = (data.images || []).map(img => ({
        id: img.id,
        name: img.name,
        subname: img.subName,
        iconUrl: img.imageUrl
      })) as FeatureItem[];
      
      setAboutMain({
        pageName: 'about',
        sectionName: 'ABOUT',
        attributes: {
          subtitle: data.subTitle || 'About Us',
          title: data.title || 'Grand Pittu Story',
          description: data.description || '',
          mainImageUrl: data.bgImageUrl || '',
          buttonText: data.buttonName || 'View Menu',
          features: mappedFeatures,
        },
      });

      setVideoSection({
        pageName: 'about',
        sectionName: 'ABOUT',
        attributes: {
          youtubeUrl: data.videoUrl || '',
          title: data.videoTitle || 'Our Restaurant Tour',
        },
      });

      setCtaSection({
        pageName: 'about',
        sectionName: 'ABOUT',
        attributes: {
          subtitle: data.ctaSubTitle || 'Reserve Now',
          title: data.ctaTitle || 'Book Your Table Today',
          buttonText: data.ctaButtonName || 'Make Reservation',
          backgroundImageUrl: data.ctaBgImageUrl || '',
        },
      });
      
      toast.success('About section loaded successfully');
      
    } catch (error) {
      toast.error('Failed to load about section');
    } finally {
      setIsLoading(false);
    }
  };

  const markChanged = () => setHasUnsavedChanges(true);

  // Save features: new features via add-images, edited features via features/{id}
  const saveFeatures = async (): Promise<boolean> => {
    if (!aboutSectionId) {
      return true;
    }

    const currentFeatures = (aboutMain.attributes.features as FeatureItem[]) || [];
    
    // Separate new features (no id or marked as new) from edited existing features
    const newFeatures = currentFeatures.filter(f => !f.id || f._isNew);
    const editedFeatures = currentFeatures.filter(f => f.id && f._isEdited && !f._isNew);

    try {
      // 1. Add new features via PUT /about-section/add-images/{id}
      if (newFeatures.length > 0) {
        const addImagesDto = newFeatures.map(f => ({
          name: f.name,
          subName: f.subname,
          imageUrl: f.iconUrl
        }));
        
        await adminApi.fetch(`/about-section/add-images/${aboutSectionId}`, {
          method: 'PUT',
          data: addImagesDto,
        });
        
        toast.success(`Added ${newFeatures.length} new feature(s)`);
      }

      // 2. Update edited features via PUT /about-section/features/{id}
      for (const feature of editedFeatures) {
        const updateDto = {
          id: feature.id,
          name: feature.name,
          subName: feature.subname,
          imageUrl: feature.iconUrl
        };
        
        await adminApi.fetch(`/about-section/features/${feature.id}`, {
          method: 'PUT',
          data: updateDto,
        });
      }
      
      if (editedFeatures.length > 0) {
        toast.success(`Updated ${editedFeatures.length} feature(s)`);
      }

      return true;
    } catch (error) {
      toast.error('Failed to save features');
      return false;
    }
  };

  const saveAllSections = async () => {
    setIsSaving(true);
    
    try {
      const currentFeatures = (aboutMain.attributes.features as FeatureItem[]) || [];
      
      // Main section DTO (without images for PUT - they are saved via dedicated endpoints)
      const dto = {
        sectionName: 'ABOUT',
        title: aboutMain.attributes.title as string,
        subTitle: aboutMain.attributes.subtitle as string,
        description: aboutMain.attributes.description as string,
        bgImageUrl: aboutMain.attributes.mainImageUrl as string,
        buttonName: aboutMain.attributes.buttonText as string,
        videoUrl: videoSection.attributes.youtubeUrl as string,
        videoTitle: videoSection.attributes.title as string,
        ctaSubTitle: ctaSection.attributes.subtitle as string,
        ctaTitle: ctaSection.attributes.title as string,
        ctaButtonName: ctaSection.attributes.buttonText as string,
        ctaBgImageUrl: ctaSection.attributes.backgroundImageUrl as string,
      };

      let sectionId = aboutSectionId;
      
      if (aboutSectionId) {
        // Update existing section
        await adminApi.fetch(`/about-section/${aboutSectionId}`, {
          method: 'PUT',
          data: dto,
        });
        toast.success('About section updated successfully');
        
        // Save features via dedicated endpoints for existing sections
        await saveFeatures();
      } else {
        // Create new section - include images for initial creation
        const createDto = {
          ...dto,
          images: currentFeatures.map(f => ({
            name: f.name,
            subName: f.subname,
            imageUrl: f.iconUrl
          })),
        };
        
        const createResponse = await adminApi.fetch<AboutSectionResponse>('/about-section', {
          method: 'POST',
          data: createDto,
        });
        
        if (createResponse?.id) {
          setAboutSectionId(createResponse.id);
          sectionId = createResponse.id;
        }
        toast.success('About section created successfully');
      }

      const user = await authService.getProfile();
      
      versionHistoryService.track({
        entityType: 'about-section',
        entityId: sectionId?.toString() || 'new',
        action: aboutSectionId ? 'update' : 'create',
        changes: { dto: { old: {}, new: dto } },
        snapshot: dto as unknown as Record<string, unknown>,
        user: user?.name || 'Unknown',
      });
      
      // Reload to get updated data from server
      await loadAboutSection();
      
      setHasUnsavedChanges(false);
      
    } catch (error) {
      toast.error('Failed to save sections');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const response = await cmsApiService.uploadImage(file, 'about-section');
      const imageUrl = response.url || response.imageUrl || response;
      
      if (!imageUrl || typeof imageUrl !== 'string') {
        throw new Error('No URL returned from upload');
      }
      
      toast.success('Image uploaded successfully');
      return imageUrl;
    } catch (error) {
      toast.error('Failed to upload image');
      throw error;
    }
  };

  // Handler for feature changes - marks new items as _isNew and edited items as _isEdited
  const handleFeaturesChange = (items: FeatureItem[]) => {
    // Get current features to compare
    const currentFeatures = (aboutMain.attributes.features as FeatureItem[]) || [];
    
    // Process items to mark them appropriately
    const processedItems = items.map(item => {
      // If item has no id, it's a new item
      if (!item.id) {
        return { ...item, _isNew: true };
      }
      
      // If item has an id, check if it was edited
      const existingItem = currentFeatures.find(f => f.id === item.id);
      if (existingItem) {
        const isEdited = existingItem.name !== item.name || 
                         existingItem.subname !== item.subname || 
                         existingItem.iconUrl !== item.iconUrl;
        if (isEdited) {
          return { ...item, _isEdited: true };
        }
      }
      
      return item;
    });
    
    setAboutMain((p) => ({
      ...p,
      attributes: { ...p.attributes, features: processedItems }
    }));
    markChanged();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between backdrop-blur-sm bg-white/80 dark:bg-slate-900/80 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 dark:from-white dark:via-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
             <Info className="w-8 h-8 text-slate-700 dark:text-slate-300" />
            About Page
            </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadAboutSection} disabled={isLoading}>
            <History className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Warning Banner */}
      {hasUnsavedChanges && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <Clock className="w-5 h-5 text-amber-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  You have unsaved changes
                </p>
                <p className="text-xs text-amber-600">
                  Click Save All to persist your changes.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setHasUnsavedChanges(false);
                  loadAboutSection();
                }}
                className="text-amber-800 border-amber-300 hover:bg-amber-100"
              >
                Discard
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={saveAllSections}
                disabled={isSaving}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {isSaving ? 'Saving...' : 'Save Now'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Clock className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p>Loading about section...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Section 1: About Main */}
          <InlineSectionCard
            title="Section 1: About Main Content"
            description="Main about section with features"
            icon={<Info className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'page-content', id: 1 })}
      >
        <InlineText
          label="Subtitle"
          value={(aboutMain.attributes.subtitle as string) || ''}
          onChange={(v) => { 
            setAboutMain((p) => ({ ...p, attributes: { ...p.attributes, subtitle: v } }));
            markChanged();
          }}
        />
        <InlineText
          label="Title"
          value={(aboutMain.attributes.title as string) || ''}
          onChange={(v) => { 
            setAboutMain((p) => ({ ...p, attributes: { ...p.attributes, title: v } }));
            markChanged();
          }}
        />
        <div className="space-y-2">
          <div className="text-sm font-medium">Description</div>
          <RichTextEditor
            value={(aboutMain.attributes.description as string) || ''}
            onChange={(v) => {
              setAboutMain((p) => ({ ...p, attributes: { ...p.attributes, description: v } }));
              markChanged();
            }}
            placeholder="Write about main content description..."
            minHeight="180px"
          />
        </div>
        <InlineImage
          label="Main Image"
          value={(aboutMain.attributes.mainImageUrl as string) || ''}
          onChange={(v) => { 
            setAboutMain((p) => ({ ...p, attributes: { ...p.attributes, mainImageUrl: v } }));
            markChanged();
          }}
          onUpload={handleImageUpload}
        />
        <InlineText
          label="Button Text"
          value={(aboutMain.attributes.buttonText as string) || ''}
          onChange={(v) => { 
            setAboutMain((p) => ({ ...p, attributes: { ...p.attributes, buttonText: v } }));
            markChanged();
          }}
        />

        <div className="space-y-2">
          <InlineArray
            items={aboutMain.attributes.features as FeatureItem[]}
            onChange={handleFeaturesChange}
            label="Features"
            getDefaultItem={() => ({ name: '', subname: '', iconUrl: '', _isNew: true })}
            canAdd={true}
            canDelete={true}
            canReorder={true}
            renderItem={(item) => (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded overflow-hidden">
                  {item.iconUrl ? (
                    <img src={item.iconUrl} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-sm text-muted-foreground">{item.subname}</div>
                </div>
              </div>
            )}
            renderForm={(item, onChange, onSave, onCancel) => (
              <div className="space-y-3">
                <InlineText
                  label="Name"
                  value={(item.name as string) || ''}
                  onChange={(v) => { onChange('name', v); markChanged(); }}
                />
                <InlineText
                  label="Subname"
                  value={(item.subname as string) || ''}
                  onChange={(v) => { onChange('subname', v); markChanged(); }}
                />
                <InlineImage
                  label="Icon"
                  value={(item.iconUrl as string) || ''}
                  onChange={(v) => { onChange('iconUrl', v); markChanged(); }}
                  onUpload={handleImageUpload}
                />
                <div className="flex gap-2">
                  <Button onClick={onSave} size="sm" variant="blueBg">Done</Button>
                  <Button onClick={onCancel} variant="outline" size="sm">Cancel</Button>
                </div>
              </div>
            )}
          />
        </div>
      </InlineSectionCard>

      {/* Section 4: CTA */}
      <InlineSectionCard
        title="Section 4: CTA Section"
        description="Call to action banner"
        icon={<Zap className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'page-content', id: 4 })}
      >
        <InlineText
          label="Subtitle"
          value={(ctaSection.attributes.subtitle as string) || ''}
          onChange={(v) => { 
            setCtaSection((p) => ({ ...p, attributes: { ...p.attributes, subtitle: v } }));
            markChanged();
          }}
        />
        <InlineText
          label="Title"
          value={(ctaSection.attributes.title as string) || ''}
          onChange={(v) => { 
            setCtaSection((p) => ({ ...p, attributes: { ...p.attributes, title: v } }));
            markChanged();
          }}
        />
        <InlineText
          label="Button Text"
          value={(ctaSection.attributes.buttonText as string) || ''}
          onChange={(v) => { 
            setCtaSection((p) => ({ ...p, attributes: { ...p.attributes, buttonText: v } }));
            markChanged();
          }}
        />
        <InlineImage
          label="Background Image"
          value={(ctaSection.attributes.backgroundImageUrl as string) || ''}
          onChange={(v) => { 
            setCtaSection((p) => ({ ...p, attributes: { ...p.attributes, backgroundImageUrl: v } }));
            markChanged();
          }}
          onUpload={handleImageUpload}
        />
      </InlineSectionCard>
        </>
      )}

      {/* Save All Button - Bottom Right */}
      {!isLoading && (
        <div className="flex justify-end pt-4 border-t mt-6">
          <Button 
            onClick={saveAllSections} 
            disabled={isSaving || !hasUnsavedChanges}
            className="gap-2"
            variant="blueBg"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      )}

      {historyDialog && (
        <VersionHistoryViewer
          entityType={historyDialog.type}
          entityId={historyDialog.id}
          isOpen={true}
          onClose={() => setHistoryDialog(null)}
        />
      )}
    </div>
  );
}
